import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Location, Room } from '@era/locations-sdk';
import { CategoryStoreSelectors, DeviceStoreModels } from '@era/stores';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, Subject, Subscription, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LocationSelectContainerComponent } from '../select-location/location-select/location-select.container';
import { RoomSelectContainerComponent } from '../select-location/room-select/room-select.container';

@Component({
  selector: 'era-device-link-selector',
  templateUrl: './device-link-selector.component.html',
  styleUrls: ['./device-link-selector.component.scss'],
  imports: [LocationSelectContainerComponent, ReactiveFormsModule, RoomSelectContainerComponent, AsyncPipe],
})
export class DeviceLinkSelectorComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public selection: DeviceStoreModels.SelectedLocation;
  @Input() public isSpare = undefined;
  @Input() public isHousekeeping = false;
  @Input() public isStaff = false;
  @Input() public isFrontDesk = false;
  @Input() public isBar = false;
  @Input() public isKiosk = false;
  @Output() public readonly selectionChange: EventEmitter<DeviceStoreModels.SelectedLocation> =
    new EventEmitter<DeviceStoreModels.SelectedLocation>();

  private readonly locationIdSubject: Subject<string> = new ReplaySubject(1);

  public readonly location$: Observable<Location>;
  public readonly spareControl = new UntypedFormControl(true);

  @Input() public isLocationEnabled = true;

  private readonly subscriptions: Subscription[] = [];

  constructor(private readonly locationStore: Store<Location>) {
    this.location$ = combineLatest([this.locationIdSubject]).pipe(
      switchMap(([locationId]) =>
        this.locationStore
          .select(CategoryStoreSelectors.selectAllCategoryEntitiesByType('Location'))
          .pipe(map((locations) => locations.find((location) => location.id === locationId)))
      )
    );
  }

  ngOnChanges() {
    if (this.selection) {
      this.locationIdSubject.next(this.selection.locationId);
      this.spareControl.setValue(this.isSpare || (this.isSpare !== false && !this.selection.roomId));
    }
  }

  public roomUpdated(event: Room) {
    this.selection.roomId = event.id;
    this.emitSelection();
  }

  public locationUpdated(event: Location) {
    this.locationIdSubject.next(event.id);
    this.selection.locationId = event.id;
    this.emitSelection();
  }

  private emitSelection() {
    const requiresLocationIdOnly =
      this.spareControl.value || this.isHousekeeping || this.isStaff || this.isFrontDesk || this.isBar || this.isKiosk;

    if (requiresLocationIdOnly) {
      this.selection.roomId = undefined;
    }

    this.selection.isValid = requiresLocationIdOnly
      ? !!this.selection.locationId
      : !!this.selection.locationId && !!this.selection.roomId;
    this.selectionChange.emit(this.selection);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.spareControl.valueChanges.subscribe(() => {
        this.emitSelection();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
